/* Plantica — product listing (sections/plantica-collection.liquid)

   The page works without any of this: the filters are a GET form with a submit
   button in <noscript>, the chips and „Resetează" are links, „Încarcă încă" is a
   link to the next page. The script only keeps the reader on the page:

   - a filter or the sort applies as it changes, and the section is re-rendered
     from the server (Shopify's section rendering), not rebuilt here — the counts,
     the chips and the grid come back exactly as Liquid draws them;
   - „Încarcă încă" fetches the next page and puts its cards under these;
   - on a phone and a tablet, „Filtrează" opens the filters as a panel;
   - the sort menu (a <details> whose options submit the form) closes on a
     click elsewhere and on Escape, and its choice goes into the address.

   The theme's quick-view modals move themselves to <body> when they open, so a
   re-render would leave the old ones behind with the same ids as the new; they
   are cleared first.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  /* ---------- the price range: two thumbs on one track ---------- */

  if (!customElements.get('plantica-price-range')) {
    class PlanticaPriceRange extends HTMLElement {
      connectedCallback() {
        this.low = this.querySelector('[data-range-min]');
        this.high = this.querySelector('[data-range-max]');
        this.lowInput = this.querySelector('[data-input-min]');
        this.highInput = this.querySelector('[data-input-max]');
        this.lowLabel = this.querySelector('[data-label-min]');
        this.highLabel = this.querySelector('[data-label-max]');
        this.top = Number(this.dataset.max) || Number(this.high.max) || 0;

        this.addEventListener('input', this.onInput);
        // Before the form hears the same `change`: this element sits between
        // the input and the form, so it writes the values the form will read.
        this.addEventListener('change', this.onChange);
        this.paint();
      }

      onInput = (event) => {
        let low = Number(this.low.value);
        let high = Number(this.high.value);
        if (low > high) {
          if (event.target === this.low) this.low.value = high;
          else this.high.value = low;
        }
        this.paint();
      };

      onChange = () => {
        const low = Number(this.low.value);
        const high = Number(this.high.value);
        // At the ends of the track the filter is off, not set to its own limit.
        this.lowInput.value = low > 0 ? String(low) : '';
        this.highInput.value = high < this.top ? String(high) : '';
      };

      paint() {
        const low = Number(this.low.value);
        const high = Number(this.high.value);
        const top = this.top || 1;
        this.style.setProperty('--plantica-range-from', `${(low / top) * 100}%`);
        this.style.setProperty('--plantica-range-to', `${100 - (high / top) * 100}%`);
        if (this.lowLabel) this.lowLabel.textContent = `${low} lei`;
        if (this.highLabel) this.highLabel.textContent = `${high} lei`;
        // Pushed to the top of the track, the low thumb would hide under the
        // high one and could not be grabbed back.
        this.low.style.zIndex = low >= top - 1 ? '3' : '';
      }
    }

    customElements.define('plantica-price-range', PlanticaPriceRange);
  }

  /* ---------- the listing ---------- */

  if (customElements.get('plantica-collection')) return;

  const PHONE = window.matchMedia('(max-width: 989px)');

  class PlanticaCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.controller = null;
      this.timer = null;

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopstate);
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopstate);
      document.removeEventListener('click', this.onDocumentClick);
      this.unlock();
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    get panel() {
      return this.querySelector('[data-filters]');
    }

    /* ---------- what triggers a render ---------- */

    onChange = (event) => {
      const form = this.form;
      if (!form) return;
      const target = event.target;
      if (!(form.contains(target) || target.form === form)) return;

      // The price waits for the hand to settle; a checkbox goes at once.
      const wait = target.type === 'range' ? 350 : 0;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.apply(), wait);
    };

    onSubmit = (event) => {
      if (!event.target.matches('[data-facets-form]')) return;
      event.preventDefault();

      // A sort option: the menu closes, and the keyboard goes back to its pill,
      // which comes back from the re-render with the same id.
      const submitter = event.submitter;
      const menu = submitter && submitter.closest('[data-sort]');
      if (menu) {
        const pill = menu.querySelector('summary');
        menu.open = false;
        if (pill) pill.focus({ preventScroll: true });
      }
      this.apply(submitter);
    };

    // The sort menu closes when the click lands anywhere else.
    onDocumentClick = (event) => {
      this.querySelectorAll('[data-sort][open]').forEach((menu) => {
        if (!menu.contains(event.target)) menu.open = false;
      });
    };

    onClick = (event) => {
      const link = event.target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.render(link.href, true);
        return;
      }

      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      if (event.target.closest('[data-filters-open]')) {
        this.open();
        return;
      }

      if (event.target.closest('[data-filters-close]')) {
        this.close();
      }
    };

    onKeydown = (event) => {
      if (event.key !== 'Escape') return;

      const menu = event.target.closest && event.target.closest('[data-sort][open]');
      if (menu) {
        menu.open = false;
        const pill = menu.querySelector('summary');
        if (pill) pill.focus();
        return;
      }

      if (this.panel && this.panel.classList.contains('is-open')) {
        this.close();
      }
    };

    onPopstate = () => {
      this.render(window.location.href, false);
    };

    apply(submitter) {
      const form = this.form;
      if (!form) return;
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (value !== '') params.append(key, value);
      }
      // FormData leaves out the button that submitted; a sort option is one,
      // and it replaces the order the hidden input carried.
      if (submitter && submitter.name) params.set(submitter.name, submitter.value);
      const base = form.getAttribute('action') || window.location.pathname;
      const query = params.toString();
      this.render(query ? `${base}?${query}` : base, true);
    }

    /* ---------- fetching a version of this section ---------- */

    async fetchSection(url) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);

      const response = await fetch(target.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('plantica-collection');
    }

    async render(url, push) {
      this.classList.add('is-loading');
      const focusedId = document.activeElement && document.activeElement.id;

      try {
        const fresh = await this.fetchSection(url);
        if (!fresh) throw new Error('no section');

        this.clearModals();

        // The panel keeps its element — and so stays open on a phone — while
        // what is inside it is swapped.
        const facets = this.querySelector('[data-facets]');
        const freshFacets = fresh.querySelector('[data-facets]');
        if (facets && freshFacets) facets.innerHTML = freshFacets.innerHTML;

        const results = this.querySelector('[data-results]');
        const freshResults = fresh.querySelector('[data-results]');
        if (results && freshResults) results.innerHTML = freshResults.innerHTML;

        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          clean.searchParams.delete('page');
          window.history.pushState({}, '', clean.toString());
        }

        // The control just used comes back with the same id: put the keyboard
        // back on it rather than at the top of the page.
        if (focusedId) {
          const again = document.getElementById(focusedId);
          if (again) again.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Something went wrong between here and the server: the plain link
        // still works, so take it.
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    /* ---------- „Încarcă încă" ---------- */

    async loadMore(button) {
      button.setAttribute('aria-busy', 'true');
      try {
        const fresh = await this.fetchSection(button.href);
        if (!fresh) throw new Error('no section');

        const grid = this.querySelector('[data-grid]');
        const freshGrid = fresh.querySelector('[data-grid]');
        const cells = freshGrid ? Array.from(freshGrid.children) : [];
        const first = cells[0];
        cells.forEach((cell) => grid.appendChild(cell));

        const foot = this.querySelector('[data-foot]');
        const freshFoot = fresh.querySelector('[data-foot]');
        if (foot && freshFoot) foot.replaceWith(freshFoot);

        // The address follows, so a reload lands where the reader is.
        const address = new URL(button.href, window.location.origin);
        address.searchParams.delete('section_id');
        window.history.replaceState({}, '', address.toString());

        // The keyboard goes to the first new product, not back to the top.
        const link = first && first.querySelector('.plantica-card__title a, a');
        if (link) link.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = button.href;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    clearModals() {
      if (!this.sectionId) return;
      document.querySelectorAll(`quick-view-modal[id$="-${this.sectionId}"]`).forEach((modal) => {
        if (!this.contains(modal)) modal.remove();
      });
    }

    /* ---------- the panel, on a phone and a tablet ---------- */

    open() {
      const panel = this.panel;
      if (!panel || !PHONE.matches) return;
      panel.classList.add('is-open');
      const scrim = this.querySelector('.plantica-collection__scrim');
      if (scrim) scrim.hidden = false;
      this.querySelectorAll('[data-filters-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.lock();
      const close = panel.querySelector('.plantica-facets__close');
      if (close) close.focus({ preventScroll: true });
    }

    close() {
      const panel = this.panel;
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      const scrim = this.querySelector('.plantica-collection__scrim');
      if (scrim) scrim.hidden = true;
      const toggle = this.querySelector('[data-filters-open]');
      this.querySelectorAll('[data-filters-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.unlock();
      if (toggle) toggle.focus({ preventScroll: true });
    }

    lock() {
      document.documentElement.style.overflow = 'hidden';
    }

    unlock() {
      document.documentElement.style.removeProperty('overflow');
    }
  }

  customElements.define('plantica-collection', PlanticaCollection);
})();
